import React, { useReducer, createContext } from 'react';
import { useEffectOnce } from 'react-use';
import { isEmpty } from 'lodash';
import axios from 'axios';
import Alert from '../helpers/alert';
import { ERR_MSG } from '../globalConstants';
import type {
  SessionState,
  SessionAction,
  SessionContextValue,
} from '../types';

const Session = createContext<SessionContextValue>(undefined as any);
const authenticatedUser = (document.querySelector('#root') as HTMLElement)
  .dataset.auth;
(document.querySelector('#root') as HTMLElement).dataset.auth = '';

const initialState: SessionState = {
  user: !isEmpty(authenticatedUser) ? JSON.parse(authenticatedUser!) : null,
  loading: false,
  originalPath: window.location.pathname,
};

export const ACTIONS = {
  REGISTERING_USER: 'REGISTERING_USER' as const,
  SET_USER: 'SET_USER' as const,
  BAPTIZE_USER: 'BAPTIZE_USER' as const,
  UPDATE_USER: 'UPDATE_USER' as const,
  REMOVE_USER: 'REMOVE_USER' as const,
  FAILED_REGISTRATION: 'FAILED_REGISTRATION' as const,
};

const LOGOUT_EVENT = 'logout_all_windows';

const reducer = (
  state: SessionState = initialState,
  { type, value }: SessionAction,
): SessionState => {
  switch (type) {
    case ACTIONS.REGISTERING_USER:
      return { ...state, loading: true };
    case ACTIONS.BAPTIZE_USER:
      return {
        ...state,
        user: { ...value, isFirstTime: true },
        loading: false,
      };
    case ACTIONS.FAILED_REGISTRATION:
      return { ...state, loading: false };
    case ACTIONS.SET_USER:
      return { ...state, user: value, loading: false };
    case ACTIONS.UPDATE_USER:
      return { ...state, user: value };
    case ACTIONS.REMOVE_USER:
      return { ...state, user: null };

    default:
      return state;
  }
};

const channel = new BroadcastChannel('logout');

function SessionProvider(props: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const clearSessionContext = async () => {
    dispatch({ type: ACTIONS.REMOVE_USER });
  };

  const logout = async () => {
    try {
      const { data } = await axios.post('/auth/logout');
      Alert.success(data.message);
      clearSessionContext();
      channel.postMessage(LOGOUT_EVENT);
    } catch (error) {
      Alert.error(ERR_MSG);
    }
  };

  useEffectOnce(() => {
    channel.onmessage = (msg) => {
      if (msg.data === LOGOUT_EVENT) {
        clearSessionContext();
      }
    };
  });

  return (
    <Session.Provider value={{ state, dispatch, clearSessionContext, logout }}>
      {props.children}
    </Session.Provider>
  );
}

const SessionConsumer = Session.Consumer;

export { Session, SessionProvider, SessionConsumer };
